import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Banner, Card, Select, cn } from '@cou/admin-ui';
import { AdminShell } from '../shell/AdminShell';
import { useAdminAuth } from '../session/admin-auth-context';
import {
  defaultMetricsClient,
  type MetricsClient,
  type SettlementMetrics,
} from '../settlement/metrics-client';
import {
  defaultContestsClient,
  type AdminContestWithEntryCount,
  type ContestsClient,
} from '../contests/contests-client';

/**
 * The Settlement workbench (admin-ui.md → Settlement), as far as CAE-269 builds it: the API-24
 * monitoring strip (COU-S35), global and filterable to a Contest. The corrections queue and the
 * manual actions are API-11 and land with the settlement tooling (COU-D18); this screen says so
 * rather than showing an empty queue that reads as "nothing to correct".
 *
 * The strip is counts only, grouped as the design lays them out — Entries, Staged results, Awards.
 * Each number's queue lives elsewhere: corrections in API-11, failed awards in the Awards section,
 * which the failed count links into.
 */

export interface SettlementScreenProps {
  /** Injected for tests; defaults to the real API-24 client. */
  readonly client?: MetricsClient;
  /** Injected for tests; sources the contest-filter chips (API-09). */
  readonly contestsClient?: ContestsClient;
}

/**
 * How many contests to list in the filter dropdown. A dropdown (not chips) so the control stays
 * usable across a season of daily contests; "All contests" is the default and always aggregates
 * every contest globally regardless of this cap — the cap only bounds which contests are
 * individually selectable.
 */
const CONTEST_OPTION_LIMIT = 100;

export function SettlementScreen({
  client = defaultMetricsClient,
  contestsClient = defaultContestsClient,
}: SettlementScreenProps) {
  const { getToken } = useAdminAuth();
  const [metrics, setMetrics] = useState<SettlementMetrics | null>(null);
  const [contests, setContests] = useState<readonly AdminContestWithEntryCount[]>([]);
  const [contestId, setContestId] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);

  // The contest-filter source loads once — the strip is "global and filterable to a Contest".
  useEffect(() => {
    const token = getToken();
    if (token === null) return;
    let cancelled = false;
    void (async () => {
      const result = await contestsClient.listContests(token, { limit: CONTEST_OPTION_LIMIT });
      if (!cancelled && result.ok) setContests(result.contests);
    })();
    return () => {
      cancelled = true;
    };
  }, [contestsClient, getToken]);

  const load = useCallback(async () => {
    const token = getToken();
    if (token === null) return;
    const result = await client.getSettlementMetrics(
      token,
      contestId === 'all' ? undefined : contestId,
    );
    if (result.ok) {
      setMetrics(result.metrics);
      setError(null);
    } else {
      // Never render zeroes on a failed read: "0 stuck" is an answer, and it would be a lie.
      setError(result.message ?? 'The settlement metrics failed to load.');
      setMetrics(null);
    }
  }, [client, getToken, contestId]);

  useEffect(() => {
    void load();
  }, [load]);

  const contestOptions = [
    { value: 'all', label: 'All contests' },
    ...contests.map((c) => ({ value: c.id, label: c.name })),
  ];

  return (
    <AdminShell
      title="Settlement"
      subtitle="Settlement health across contests. Corrections and manual actions land with the settlement tooling."
    >
      <Select
        label="Contest"
        className="mt-1 w-[280px]"
        options={contestOptions}
        value={contestId}
        onChange={setContestId}
      />

      {error !== null && (
        <Banner data-testid="settlement-error" variant="danger" className="mt-4">
          {error}
        </Banner>
      )}

      {metrics !== null && (
        <>
          <div
            data-testid="settlement-strip"
            className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3"
          >
            <StatGroup title="Entries">
              <Stat
                testId="metric-entries-settled"
                label="Settled"
                value={metrics.entries.settled}
              />
              <Stat
                testId="metric-entries-pending"
                label="Pending"
                value={metrics.entries.pending}
                tone="warn"
              />
              <Stat
                testId="metric-entries-stuck"
                label="Stuck"
                value={metrics.entries.stuck}
                tone="danger"
              />
            </StatGroup>

            <StatGroup title="Staged results">
              <Stat testId="metric-staged-held" label="Held" value={metrics.stagedResults.held} />
              <Stat
                testId="metric-staged-matured"
                label="Matured"
                value={metrics.stagedResults.matured}
              />
              <Stat
                testId="metric-staged-corrected"
                label="Corrected"
                value={metrics.stagedResults.corrected}
                tone="warn"
              />
            </StatGroup>

            <StatGroup title="Awards">
              <Stat
                testId="metric-awards-failed"
                label="Failed"
                value={metrics.awards.failed}
                tone="danger"
                href="/awards?status=Failed"
              />
              <Stat
                testId="metric-awards-retryable"
                label="Retryable"
                value={metrics.awards.retryable}
                tone="warn"
              />
            </StatGroup>
          </div>

          <p data-testid="settlement-as-of" className="mt-2 text-xs text-ink-4">
            Counted {new Date(metrics.asOf).toLocaleString()}
          </p>
        </>
      )}
    </AdminShell>
  );
}

/** One grouped monitoring card: an uppercase title over an evenly-divided row of stats. */
function StatGroup({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <Card className="gap-4 p-5">
      <span className="font-ui text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-3">
        {title}
      </span>
      <div className="mt-3 grid grid-cols-3 divide-x divide-line">{children}</div>
    </Card>
  );
}

/**
 * One stat cell. A `tone` colours the number only when it is non-zero — a coloured zero would cry
 * wolf; a zero reads as calm grey (mirrors KpiCell's idle treatment). `href` makes the number a
 * link into the queue that owns it (the failed-award count → the Awards section).
 */
function Stat({
  testId,
  label,
  value,
  tone = 'default',
  href,
}: {
  readonly testId: string;
  readonly label: string;
  readonly value: number;
  readonly tone?: 'default' | 'warn' | 'danger';
  readonly href?: string;
}) {
  const colour =
    value === 0
      ? 'text-ink-4'
      : tone === 'warn'
        ? 'text-warn'
        : tone === 'danger'
          ? 'text-danger'
          : 'text-ink';
  const numberClass = cn(
    'font-mono text-[24px] font-semibold leading-none tracking-[-0.02em] tabular-nums',
    colour,
  );
  return (
    <div data-testid={testId} className="flex flex-col gap-1.5 px-4 first:pl-0">
      {href !== undefined ? (
        <Link to={href} data-testid={`${testId}-link`} className={numberClass}>
          {value}
        </Link>
      ) : (
        <span className={numberClass}>{value}</span>
      )}
      <span className="font-ui text-[12px] text-ink-4">{label}</span>
    </div>
  );
}
